import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SelectionReadingPartCard from '../components/cards/selection-reading-part-card';
import MessageModal from '../components/modals/MessageModal';
import MicrophonePrimerModal from '../components/modals/MicrophonePrimerModal';

import { useStoreByName } from '../../hooks/use-stores.hooks';
import { useHomeOnboardingTour, wasTourCompleted } from '../../hooks/use-onboarding-tour.hooks';
import { MicrophoneService } from '../../services/core';
import { validateToken } from '../../services/api';
import { observer } from 'mobx-react-lite';
import HomeworkIcon from '@mui/icons-material/HomeWork';
import { Info } from '@mui/icons-material';
import IconTeacher from '../../assets/SVGComponents/iconTeacher';
import { LoaderComponent } from '../components/core/LoaderComponent';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUserData, userData } = useStoreByName('userStore');
  const userStore = useStoreByName('userStore');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showMobileNotice, setShowMobileNotice] = useState<boolean>(false);
  const [showMicPrimer, setShowMicPrimer] = useState<boolean>(false);

  // בקשת הרשאת מיקרופון פעם אחת בלבד, כאן בדף הבית ולא באמצע השינון
  const maybeAskForMicrophone = useCallback(async () => {
    if (MicrophoneService.wasPrimed()) {
      return;
    }

    const state = await MicrophoneService.getPermissionState();
    if (state === 'prompt') {
      setShowMicPrimer(true);
    } else {
      // כבר אושר, כבר נדחה, או שהדפדפן לא תומך בבדיקה - אין מה לשאול
      MicrophoneService.markPrimed();
    }
  }, []);

  // סיור הדרכה חד-פעמי למשתמשים חדשים, אחרי שהדף נטען
  useHomeOnboardingTour(!isLoading, maybeAskForMicrophone);

  // משתמש שכבר ראה את הסיור לא יקבל אותו שוב - ולכן נשאל אותו כאן
  useEffect(() => {
    if (isLoading || !wasTourCompleted('home')) {
      return;
    }
    maybeAskForMicrophone();
  }, [isLoading, maybeAskForMicrophone]);

  const GUEST_USER_ID = 544;
  const isGuestUser = userData?.personalInfo?.id === GUEST_USER_ID;

  // פונקציה לזיהוי מובייל
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  useEffect(() => {
    const initializeUser = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        setIsLoading(true);
        await validateToken();
        await getUserData();

        // בדיקה אם זה מובייל והאם כבר הוצגה ההודעה
        if (isMobile() && !sessionStorage.getItem('mobileNoticeShown')) {
          setShowMobileNotice(true);
          sessionStorage.setItem('mobileNoticeShown', 'true');
        }
      } catch (error) {
        console.error('שגיאה באימות או במשיכת נתונים:', error);
        navigate('/');
        return;
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [getUserData, navigate, t]);

  if (isLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      <main className="body home-page">
        <div className="head-section">
          <h4 className="welcome-message"> {`${t('niceToSeeYou')},`}</h4>
          <h3>{`${userData?.personalInfo?.name}!`}</h3>

          {isGuestUser && (
            <div className="guest-user-message-card">
              <Info className="icon" />
              <p>
                {t('guestUserHomeMessage')}{' '}
                <button
                  onClick={() => {
                    userStore.userData = null;
                    localStorage.clear();
                    sessionStorage.clear();
                    navigate('/');
                  }}
                >
                  {t('guestUserHomeLink')}
                </button>
              </p>
            </div>
          )}

          <div className="user-details">
            {userData?.personalInfo?.school && (
              <div className="detail-row">
                <div className="icon-circle">
                  <HomeworkIcon />
                </div>
                <div className="detail-text">
                  <span className="value">
                    בית ספר <b>{userData.personalInfo.school.name}</b>
                  </span>
                </div>
              </div>
            )}

            {userData?.personalInfo?.grades && userData.personalInfo.grades.length > 0 && (
              <div className="detail-row">
                <div className="icon-circle">
                  <IconTeacher />
                </div>
                <div className="detail-text">
                  <span className="value">
                    כיתה <b>{userData.personalInfo.grades[0].name}</b>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <section>
          <SelectionReadingPartCard />
        </section>
      </main>

      <MicrophonePrimerModal isOpen={showMicPrimer} onClose={() => setShowMicPrimer(false)} />
    </>
  );
};

export default observer(Home);
